import type {
  CustomerPurchasedSubscriptionListItem,
  ListCustomerPurchasedSubscriptionsResponse,
} from "./types";
import {resolveStoreSubscriptionIdForReviews} from "../shared/resolve-store-subscription-id";

function firstFiniteNumber(...vals: unknown[]): number | undefined {
  for (const v of vals) {
    if (typeof v === "number" && Number.isFinite(v)) {
      return v;
    }
    if (typeof v === "string" && v.trim() !== "") {
      const n = Number(v);
      if (Number.isFinite(n)) {
        return n;
      }
    }
  }
  return undefined;
}

function firstCurrencyString(...vals: unknown[]): string | undefined {
  for (const v of vals) {
    if (typeof v === "string" && v.trim() !== "") {
      return v.trim();
    }
  }
  return undefined;
}

function mapListItem(raw: unknown): CustomerPurchasedSubscriptionListItem {
  if (!raw || typeof raw !== "object") {
    return {id: 0, storeSubscriptionId: 0, name: ""};
  }
  const o = raw as Record<string, unknown>;
  const coach =
    o.coach && typeof o.coach === "object" ? (o.coach as Record<string, unknown>) : null;
  const tier =
    o.tier && typeof o.tier === "object"
      ? (o.tier as Record<string, unknown>)
      : o.currentTier && typeof o.currentTier === "object"
      ? (o.currentTier as Record<string, unknown>)
      : null;

  const priceFromTier = tier
    ? firstFiniteNumber(tier.defaultPrice, tier.price, tier.amount, tier.callPrice)
    : undefined;
  const currencyFromTier = tier
    ? firstCurrencyString(tier.defaultCurrency, tier.currency, tier.callPriceCurrency)
    : undefined;

  const price = firstFiniteNumber(
    o.defaultPrice,
    o.price,
    o.amount,
    o.currentAmount,
    o.recurringPrice,
    priceFromTier
  );
  const priceCurrency = firstCurrencyString(
    o.defaultCurrency,
    o.currency,
    o.callPriceCurrency,
    currencyFromTier
  );

  const subscriptionNameFromApi =
    typeof o.subscriptionName === "string" && o.subscriptionName.trim() !== ""
      ? o.subscriptionName.trim()
      : undefined;

  const customerEnrollmentExplicit = Number(
    o.customerSubscriptionId ?? o.customer_subscription_id ?? 0
  );
  const enrollmentFallback = Number(o.id ?? 0);
  const enrollmentId =
    customerEnrollmentExplicit > 0 ? customerEnrollmentExplicit : enrollmentFallback;

  const catalogResolved = resolveStoreSubscriptionIdForReviews(
    o,
    enrollmentId > 0 ? enrollmentId : 0
  );
  const storeSubscriptionId = catalogResolved > 0 ? catalogResolved : enrollmentId;

  return {
    id: enrollmentId,
    storeSubscriptionId,
    subscriptionName: subscriptionNameFromApi,
    name: String(o.subscriptionName ?? o.name ?? o.title ?? ""),
    slug: typeof o.slug === "string" ? o.slug : undefined,
    description: typeof o.description === "string" ? o.description : undefined,
    status: o.status as string | number | undefined,
    createdAt:
      typeof o.createdAt === "string"
        ? o.createdAt
        : typeof o.created_at === "string"
        ? o.created_at
        : undefined,
    updatedAt:
      typeof o.updatedAt === "string"
        ? o.updatedAt
        : typeof o.updated_at === "string"
        ? o.updated_at
        : undefined,
    coverImage:
      typeof o.coverImage === "string"
        ? o.coverImage
        : typeof o.cover_image === "string"
        ? o.cover_image
        : undefined,
    coachName:
      typeof o.coachName === "string"
        ? o.coachName
        : typeof o.coach_name === "string"
        ? o.coach_name
        : coach
        ? String(coach.displayName ?? coach.name ?? "")
        : undefined,
    coachId:
      typeof o.coachId === "number"
        ? o.coachId
        : typeof o.coach_id === "number"
        ? o.coach_id
        : coach && typeof coach.id === "number"
        ? coach.id
        : undefined,
    ...(price !== undefined ? {price} : {}),
    ...(priceCurrency ? {priceCurrency} : {}),
  };
}

function extractPage(inner: unknown): {
  items: unknown[];
  total: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
} {
  if (!inner || typeof inner !== "object") {
    return {items: [], total: 0, hasNextPage: false, hasPrevPage: false};
  }
  const root = inner as Record<string, unknown>;

  const tryNode = (node: unknown) => {
    if (!node || typeof node !== "object") {
      return null;
    }
    const n = node as Record<string, unknown>;
    if (Array.isArray(n.data)) {
      return {
        items: n.data,
        total: Number(n.total ?? n.totalCount ?? n.data.length),
        hasNextPage: Boolean(n.hasNextPage ?? n.has_next_page),
        hasPrevPage: Boolean(n.hasPrevPage ?? n.has_prev_page),
      };
    }
    if (Array.isArray(n.subscriptions)) {
      return {
        items: n.subscriptions,
        total: Number(n.total ?? n.subscriptions.length),
        hasNextPage: Boolean(n.hasNextPage),
        hasPrevPage: Boolean(n.hasPrevPage),
      };
    }
    return null;
  };

  const fromData = tryNode(root.data);
  if (fromData) {
    return fromData;
  }
  const fromRoot = tryNode(root);
  if (fromRoot) {
    return fromRoot;
  }
  if (Array.isArray(root.data)) {
    return {
      items: root.data,
      total: root.data.length,
      hasNextPage: false,
      hasPrevPage: false,
    };
  }
  return {items: [], total: 0, hasNextPage: false, hasPrevPage: false};
}

export function normalizeListCustomerSubscriptionsResponse(
  raw: unknown
): ListCustomerPurchasedSubscriptionsResponse {
  const body =
    raw && typeof raw === "object" && "data" in raw ? (raw as {data: unknown}).data : raw;
  const {items, total, hasNextPage, hasPrevPage} = extractPage(body);
  return {
    subscriptions: items
      .map(mapListItem)
      .filter((s) => Number.isFinite(s.id) && s.id > 0),
    total,
    hasNextPage,
    hasPrevPage,
  };
}
